import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "../components/layout";
import CreatePostModal from "../components/post/CreatePostModal";
import CreateLiveRoomModal from "../components/modals/CreateLiveRoomModal";
import EditLiveRoomModal from "../components/modals/EditLiveRoomModal";
import api from "../services/api";
import { getAssetUrl } from "../services/helper";
import ChatWindow from "../components/message/ChatWindow";
import { getChatConfig } from "../components/message/chatConfigs";
import { useSearchParams } from "react-router-dom";
import { mergeMessages } from "../components/message/useChatWindow";
import { getSocket } from "../services/socket";

const ConvoRow = ({ chat, isSelected, onClick }) => {
  const isUnread = Number(chat?.unread_count || 0) > 0;

  const messageTime = chat?.last_message_at
    ? new Date(chat.last_message_at).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })
    : "";

  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-4 py-3 transition-colors hover:bg-gray-50 ${isSelected ? "bg-gray-100" : ""
        }`}
    >
      <div className="flex gap-3">
        <img
          src={getAssetUrl(chat?.chat_avatar)}
          alt={chat?.chat_name}
          className="w-14 h-14 rounded-full object-cover flex-shrink-0"
        />

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p
              className={`truncate ${isUnread ? "font-bold text-gray-900" : "font-semibold text-gray-800"
                }`}
            >
              {chat?.chat_name}
            </p>

            {messageTime && (
              <span
                className={`text-xs ${isUnread
                  ? "text-[#1F3A60] font-semibold"
                  : "text-gray-500"
                  }`}
              >
                {messageTime}
              </span>
            )}
          </div>

          <div className="flex items-center justify-between gap-2 mt-1">
            <p
              className={`truncate text-sm ${isUnread
                ? "font-medium text-gray-900"
                : "text-gray-600"
                }`}
            >
              {chat?.last_message || "Start a conversation"}
            </p>

            {isUnread && (
              <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-[#1F3A60] text-white text-[11px] flex items-center justify-center">
                {chat.unread_count > 99 ? "99+" : chat.unread_count}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
};
// const ConvoRow = ({ chat, isSelected, onClick }) => {
//   const isUnread = chat.unread_count > 0;
//   return (
//     <button onClick={onClick} className={`... ${isSelected ? "bg-gray-100" : ""}`}>
//       <div className="flex gap-3">
//         <img src={getAssetUrl(chat.chat_avatar)} className="w-14 h-14 rounded-full object-cover" />
//         <div className="flex-1 min-w-0">
//           <div className="flex justify-between">
//             <p className={`truncate ${isUnread ? "font-bold" : "font-semibold"}`}>{chat.chat_name}</p>
//             <span className="text-xs text-gray-500">
//               {new Date(chat.last_message_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
//             </span>
//           </div>
//           <p className={`text-sm truncate ${isUnread ? "font-bold text-gray-900" : "font-normal text-gray-600"}`}>
//             {chat.last_message}
//           </p>
//         </div>
//         {/* {isUnread && <span className="w-2.5 h-2.5 rounded-full bg-[#1F3A5F] self-center" />} */}
//       </div>
//     </button>
//   );
// };

const MessagesPage = () => {
  const [selectedChat, setSelectedChat] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showCreateRoom, setShowCreateRoom] = useState(false);
  const [showEditRoom, setShowEditRoom] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [privateChats, setPrivateChats] = useState([]);
  const queryClient = useQueryClient();
  const hasHandledDeepLink = useRef(false);

  // Kept separate per chat type (not per component) — this is what
  // actually fixes "previous chat looks empty on revisit": these caches
  // live above ChatWindow and are never cleared on switch, only added to.
  const [roomMessagesByChat, setRoomMessagesByChat] = useState({});
  const [privateMessagesByChat, setPrivateMessagesByChat] = useState({});

  const { data: chatsData } = useQuery({
    queryKey: ["all_chats"],
    queryFn: () => api.getMessageHistory(),
  });

  const roomChats = chatsData?.data?.live_rooms || [];
  const markMessagesReadMutation = useMutation({
    mutationFn: (otherUserId) => api.markMessagesRead(otherUserId),
    onError: (err) => console.error("Failed to mark conversation read:", err),
  });

  const markRoomReadMutation = useMutation({
    mutationFn: (roomId) => api.markRoomRead(roomId),
    onError: (err) => console.error("Failed to mark room read:", err),
  });

  // const privateChats = chatsData?.data?.single_chats || [];

  const handleCreateOrOpenPrivateChat = async (userId) => {
    try {
      const res = await api.getUser(userId);
      const user_info = res.user;

      const newChat = {
        chat_id: userId,
        user_id: userId,
        chat_name: user_info?.name,
        chat_avatar: user_info?.avatar,
        chat_type: "private",
        last_message: "",
        last_message_at: null,
        is_last_from_me: 0,
        unread_count: 0,
      };

      setPrivateChats((prev) => {
        const exists = prev.find(
          (chat) => String(chat.chat_id) === String(userId)
        );

        if (exists) {
          setSelectedChat(exists);
          return prev;
        }

        setSelectedChat(newChat);
        return [newChat, ...prev];
      });

      setShowChat(true);
    } catch (err) {
      console.error(err);
    }
  };


  // Always-current pointer for use inside the socket handlers below
  const selectedChatRef = useRef(selectedChat);
  useEffect(() => {
    selectedChatRef.current = selectedChat;
  }, [selectedChat]);
  // Deep link handler - runs when ?chat_id=xxx is present
  useEffect(() => {
    const chatId = searchParams.get("chat_id");
    if (!chatId) return;

    const foundChat = privateChats.find(
      (c) => String(c.chat_id) === String(chatId)
    );

    if (foundChat) {
      setSelectedChat(foundChat);
      setShowChat(true);
      // hasHandledDeepLink.current = true;

      // Remove chat_id from URL after handling
      setSearchParams({}, { replace: true });
    } else {
      // Try to create new private chat
      handleCreateOrOpenPrivateChat(chatId).finally(() => {
        // hasHandledDeepLink.current = true;
        setSearchParams({}, { replace: true });
      });
    }
  }, [searchParams, privateChats]); // allChats is safe because it's derived from query data

  // }, [searchParams, chatsData, privateChats]);

  useEffect(() => {
    if (chatsData?.data?.single_chats) {
      setPrivateChats(chatsData.data.single_chats);
    }
  }, [chatsData]);
  const handleSelectChat = (chat) => {
    setSelectedChat(chat);
    setShowChat(true);
    // optimistic: clear the bold instantly
    queryClient.setQueryData(["all_chats"], (old) => {
      if (!old) return old;
      const key = chat.chat_type === "room" ? "live_rooms" : "single_chats";
      return {
        ...old,
        data: {
          ...old.data,
          [key]: old.data[key].map((c) => (c.chat_id === chat.chat_id ? { ...c, unread_count: 0 } : c)),
        },
      };
    });

    // persist it server-side
    if (chat.chat_type === "room") {
      markRoomReadMutation.mutate(chat.chat_id);
    } else {
      markMessagesReadMutation.mutate(chat.chat_id);
    }
  };

  const isRoom = selectedChat?.chat_type === "room";
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const bumpUnread = (chatId, chatType, payload) => {
      const current = selectedChatRef.current;
      const isOpen = current?.chat_id === chatId && current?.chat_type === chatType;
      if (isOpen) return;

      queryClient.setQueryData(["all_chats"], (old) => {
        if (!old) return old;
        const key = chatType === "room" ? "live_rooms" : "single_chats";
        return {
          ...old,
          data: {
            ...old.data,
            [key]: old.data[key].map((c) =>
              c.chat_id === chatId
                ? { ...c, unread_count: (c.unread_count || 0) + 1, last_message: payload.message, last_message_at: payload.created_at }
                : c
            ),
          },
        };
      });
    };

    const handlePrivateMessage = (newMsg) => {
      const chatId = parseInt(newMsg.chat_id);
      setPrivateMessagesByChat((prev) => ({ ...prev, [chatId]: mergeMessages(prev[chatId] || [], [newMsg]) }));
      bumpUnread(chatId, "private", newMsg);
      queryClient.invalidateQueries({ queryKey: ["all_chats"] });
    };

    const handleRoomMessage = (newMsg) => {
      const chatId = parseInt(newMsg.room_id);
      setRoomMessagesByChat((prev) => ({ ...prev, [chatId]: mergeMessages(prev[chatId] || [], [newMsg]) }));
      bumpUnread(chatId, "room", newMsg);
      queryClient.invalidateQueries({ queryKey: ["all_chats"] });
    };

    socket.on("new_message", handlePrivateMessage);
    socket.on("new_room_message", handleRoomMessage);
    return () => {
      socket.off("new_message", handlePrivateMessage);
      socket.off("new_room_message", handleRoomMessage);
    };
  }, [queryClient]);
  return (
    <DashboardLayout onCreatePost={() => setShowCreate(true)}>
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Sidebar */}
        <div className={`${showChat ? "hidden sm:flex" : "flex"} flex-col w-full sm:w-[360px] border-r border-gray-100 bg-white flex-shrink-0 overflow-hidden`}>
          <div className="p-4">
            <button
              onClick={() => setShowCreateRoom(true)}
              className="w-full flex items-center justify-center gap-2 py-3 bg-[#1F3A5F] rounded-xl hover:bg-[#162d4a] text-[#FEDC5A] font-medium"
            >
              <span>+</span> Create Live Room
            </button>
          </div>
          <div className="flex flex-col h-full">
            <div className="h-[calc(100vh-180px)] overflow-y-auto">

              <div className="px-4 py-2 mt-6">
                <p className="text-lg font-semibold">Live Rooms</p>
              </div>
              {roomChats.length == 0 && <div className="px-4 py-2">
                <p className="text-sm text-center">No Live Rooms Found</p>
              </div>}
              {roomChats.map((chat) => (

                <ConvoRow
                  key={chat.chat_id}
                  chat={chat}
                  isSelected={selectedChat?.chat_id === chat.chat_id}
                  onClick={() => handleSelectChat(chat)}
                />
              ))}

              <div className="px-4 py-2 mt-6">
                <p className="text-lg font-semibold">Direct Messages</p>
              </div>
              {privateChats.length == 0 && <div className="px-4 py-2">
                <p className="text-sm text-center">No Chats Found</p>
              </div>}
              {privateChats.map((chat) => (
                <>
                  <ConvoRow
                    key={chat.chat_id}
                    chat={chat}
                    isSelected={selectedChat?.chat_id === chat.chat_id}
                    onClick={() => handleSelectChat(chat)}
                  />
                </>
              ))}
            </div>
          </div>
        </div>
        {/* Chat Area */}
        <div className={`${showChat ? "flex" : "hidden sm:flex"} flex-1 flex-col bg-white min-h-0`}>
          {selectedChat ? (
            <ChatWindow
              key={`${selectedChat.chat_type}-${selectedChat.chat_id}`}
              chat={selectedChat}
              config={getChatConfig(selectedChat.chat_type)}
              onBack={() => setShowChat(false)}
              onEdit={() => setShowEditRoom(true)}
              messagesByChat={isRoom ? roomMessagesByChat : privateMessagesByChat}
              setMessagesByChat={isRoom ? setRoomMessagesByChat : setPrivateMessagesByChat}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-400">Select a conversation</div>
          )}
        </div>
      </div>

      {/* Modals */}
      <CreatePostModal isOpen={showCreate} onClose={() => setShowCreate(false)} />
      <CreateLiveRoomModal isOpen={showCreateRoom} onClose={() => setShowCreateRoom(false)} />
      <EditLiveRoomModal isOpen={showEditRoom} onClose={() => setShowEditRoom(false)} roomId={selectedChat?.chat_id} />
    </DashboardLayout>
  );
};

export default MessagesPage;
